import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { readStorage, writeStorage } from '../lib/storage.ts'

export type ThemePref = 'system' | 'light' | 'dark'
const KEY = 'hrms.theme'

interface ThemeContextValue {
  pref: ThemePref
  setPref: (pref: ThemePref) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function initialPref(): ThemePref {
  const stored = readStorage(KEY)
  return stored === 'light' || stored === 'dark' ? stored : 'system'
}

/** Applies the preference to <html data-theme>. "system" removes it so the media query decides. */
function apply(pref: ThemePref) {
  const root = document.documentElement
  if (pref === 'system') delete root.dataset.theme
  else root.dataset.theme = pref
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(initialPref)

  useEffect(() => apply(pref), [pref])

  const setPref = useCallback((next: ThemePref) => {
    writeStorage(KEY, next === 'system' ? null : next)
    setPrefState(next)
  }, [])

  return <ThemeContext.Provider value={{ pref, setPref }}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}
